import { WalletProgram } from '../../../../models/interfaces';

export const MAX_IMAGE_BYTES = 1024 * 1024;
export const GOOGLE_LOGO_MIN = 660;
export const STRIP_SIZE = { width: 1125, height: 432 };       // banner propio iPhone
export const HERO_GOOGLE_SIZE = { width: 1032, height: 812 }; // banner propio Android

export const TEXT_LIMITS = { contacto: 500, terminos: 2000, premio: 200 };

export const PROGRAM_LABEL: Record<WalletProgram, string> = { puntos: 'Puntos', sellos: 'Sellos' };

// Enlace a la pestaña "Tarjeta digital" de la configuración de lealtad
export const walletConfigPath = (tipo: WalletProgram = 'puntos') => `/loyalty/settings?tab=card&programa=${tipo}`;

// crear-tarjeta responde 400 cuando el comercio no ha configurado la tarjeta de iPhone.
// Devuelve el programa a configurar, o null si el error es por otra causa.
export const walletConfigProgramFromError = (message: string): WalletProgram | null => {
    if (!/apple wallet/i.test(message ?? '')) return null;
    return /'sellos'/i.test(message) ? 'sellos' : 'puntos';
};

export const readImageSize = (file: File) => new Promise<{ width: number; height: number }>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { reject(new Error('invalid')); URL.revokeObjectURL(url); };
    img.src = url;
});

export type ImageRule =
    | { kind: 'basic' }
    | { kind: 'square'; min: number }
    | { kind: 'exact'; width: number; height: number };

// Validaciones locales para evitar viajes al API; el API vuelve a validar
export const validateImage = async (file: File, rule: ImageRule): Promise<string | null> => {
    const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    if (!isPng) return 'La imagen debe ser PNG.';
    if (file.size > MAX_IMAGE_BYTES) return `La imagen pesa ${(file.size / 1024 / 1024).toFixed(1)} MB; el máximo es 1 MB.`;
    if (rule.kind === 'basic') return null;

    let size: { width: number; height: number };
    try {
        size = await readImageSize(file);
    } catch {
        return 'No se pudo leer la imagen.';
    }
    if (rule.kind === 'square') {
        if (size.width !== size.height) return `El logo debe ser cuadrado (recibido ${size.width}×${size.height} px).`;
        if (size.width < rule.min) return `El logo debe medir al menos ${rule.min}×${rule.min} px (recibido ${size.width}×${size.height} px).`;
        return null;
    }
    if (size.width !== rule.width || size.height !== rule.height) {
        return `El banner debe medir exactamente ${rule.width}×${rule.height} px (recibido ${size.width}×${size.height} px).`;
    }
    return null;
};

const HEX_REGEX = /^#[0-9a-f]{6}$/i;
export const isHexColor = (value: string) => HEX_REGEX.test(value.trim());

// El API puede devolver colores como "rgb(r,g,b)" o "#RRGGBB"; el formulario trabaja con #RRGGBB
export const toHexColor = (value?: string | null): string => {
    if (!value) return '';
    const text = value.trim();
    if (isHexColor(text)) return text.toUpperCase();
    const match = text.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
    if (!match) return '';
    return '#' + match.slice(1, 4).map(n => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('').toUpperCase();
};

export const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

// Guía "Crea el diseño de tu tarjeta" y plantillas descargables (archivos estáticos en public/loyalty-guide)
export const GUIDE_BASE = `${process.env.PUBLIC_URL ?? ''}/loyalty-guide`;
export const TEMPLATE_URL = {
    iphone: `${GUIDE_BASE}/img/plantilla_iphone_1125x432.png`,
    android: `${GUIDE_BASE}/img/plantilla_android_1032x812.png`,
    logoAndroid: `${GUIDE_BASE}/img/plantilla_logo_android_660x660.png`,
};
